import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { REJECTIONS_TYPES_VALUES, RejectionsTypesValues } from "../shared/enums/rejections-types-enum";
import { GetRejectedTransactionsListResponse, RejectedTransactions } from "../../../../../shared/interfaces/services/rejected-credits";
import { createXLSXFile, downloadBase64Files } from "../../../../../shared/utils";
import { Form } from "antd";
import { Combo, FILE_TYPES, UserInformation } from "../../../../../shared/interfaces";
import { AuthContext, UIContext } from "../../../../../shared/contexts";
import dayjs from "dayjs";
import AutomaticDebitsRejectedService from "../../../../../shared/services/collection/automaticDebitsRejected";
import { CardSkeletons, ColSize, ErrorResults, InputFactory, RowSize } from "../../../../../shared/components";
import { RejectedDebitsCard } from "./components/RejectedCreditsCard";
import { ItemNotFound } from "../../../../../shared/components/ItemNotFound";
import { InsurancePolicyNotFound } from "../../../../../assets";
import { Grid } from "@hdi-seguros/web-components.grid";
import { getInputsFilter } from "./utils/inputsRejectedCredits";
import { mapOfRejectedPayments } from "./utils/map-of-rejected-payments";
import './index.css'
import DocumentsService from "../../../../../shared/services/documents";
import { DebitBankRejectionsPDF } from "../../../../../shared/interfaces/services/debit-bank-rejections-pdf-request";

interface AutomaticDebitsRejectedTabsProperties {
    type: RejectionsTypesValues;
}


export const AutomaticDebitsRejectedTabs = forwardRef(({ type }: AutomaticDebitsRejectedTabsProperties, ref) => {
    const [form] = Form.useForm();
    const gridRef = useRef();

    const CURRENCY_COLUMNS = [
        "Importe",
    ];

    const { isLoading, showError, setIsLoading, setShowError } = useContext(UIContext);
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [emptyStateText, setEmptyStateText] = useState('');
    const [rejectedTransactionsList, setRejectedTransactionsList] = useState<GetRejectedTransactionsListResponse>({});
    const [rejectedTransactions, setRejectedTransactions] = useState<Array<RejectedTransactions>>(rejectedTransactionsList.rechazos || []);
    const [creditCardsCompaniesCombo, setCreditCardsCompaniesCombo] = useState<Combo[]>([]);

    const authContext = useContext(AuthContext);
    const user = authContext.userInfo as UserInformation;

    const formatFilters = (filters: any) => ({
        ...filters,
        entity: filters.entity,
        year: filters.year ? dayjs(filters.year).format('YYYY') : undefined,
        month: filters.month ? dayjs(filters.month).format('M') : undefined
    });

    const initialStateFilters = useMemo(() => ({
        entity: undefined,
        year: dayjs().startOf('year'),
        month: dayjs().startOf('month')
    }), []);

    const setEmptyText = (filters: any) => {
        if (type === REJECTIONS_TYPES_VALUES.CREDITO) {
            if (filters.entity === undefined) {
                return setEmptyStateText('Para buscar rechazos de tarjeta de credito primero seleccione un tipo de tarjeta');
            }
            return setEmptyStateText(`No existen rechazos en el mes de ${dayjs(filters.month).format('MMMM')} del año ${filters.year} con la compañia ${getCreditCardsEntityDescription(filters.entity)} para el intermediario ${user.intermediarioPrincipal?.nombreInter}`);
        }
        setEmptyStateText(`No existen rechazos en el mes de ${dayjs(filters.month).format('MMMM')} del año ${filters.year} para el intermediario ${user.intermediarioPrincipal?.nombreInter}`);
    };

    const downloadExcel = () => {
        if (type === REJECTIONS_TYPES_VALUES.CREDITO) {
            if (rejectedTransactionsList?.rechazos?.length) {
                createXLSXFile(
                    mapOfRejectedPayments(rejectedTransactionsList?.rechazos, true),
                    'Rechazos de creditos bancarios',
                    {
                        currencyColumns: CURRENCY_COLUMNS,
                        titleRow: `Rechazos por Crédito Bancario ${user.intermediarioPrincipal?.codigoInter}`,
                        columnWidths: {
                            'Rama': 25,
                            'Texto': 30
                        }
                    }
                );
            }
        }
        if (type === REJECTIONS_TYPES_VALUES.DEBITO) {
            if (rejectedTransactionsList?.rechazos?.length) {
                createXLSXFile(
                    mapOfRejectedPayments(rejectedTransactionsList?.rechazos, false),
                    'Rechazos de debitos bancarios',
                    {
                        currencyColumns: CURRENCY_COLUMNS,
                        titleRow: `Rechazos por Débito Bancario ${user.intermediarioPrincipal?.codigoInter}`,
                        columnWidths: {
                            'Rama': 25,
                            'Texto': 30
                        }
                    }
                );
            }
        }
    };

    useImperativeHandle(ref, () => ({
        downloadExcel,
        downloadPDF
    }));

    const downloadPDF = async () => {
        if (!rejectedTransactionsList?.rechazos?.length) return;

        const request: DebitBankRejectionsPDF = {
            titulo: "RECHAZOS DEBITOS BANCARIOS",
            productor: user.intermediarioPrincipal?.nombreInter!,
            endoso: "0",
            tituloNombrePago: "Número de CBU",
            rechazos: (rejectedTransactionsList?.rechazos || []).map(r => ({
                rama: Number(r.rama),
                poliza: Number(r.poliza),
                numeroPago: Number(r.numeroPago),
                numeroRechazo: Number(r.numeroRechazo),
                motivoRechazo: r.motivoRechazo,
                importe: Number(r.importe),
                descripcionRama: (r as any).descripcioRama,
                endoso: Number(r.endoso),
                asegurado: r.asegurado,
                monedaOrigen: "ARS"
            }))
        }

        const response = await DocumentsService.getRejectionDebitBancaryCertificatePdf(request);

        downloadBase64Files(response.data.base64, `RechazosBancarios_${dayjs().get("millisecond")}`, FILE_TYPES.PDF)
    };

    const fetchRejectedTransactions = async (filters: any) => {
        setIsLoading(true);
        try {
            let response;
            if (type === REJECTIONS_TYPES_VALUES.CREDITO) {
                if (!filters.entity) {
                    return;
                }
                response = await AutomaticDebitsRejectedService.getRejectedCreditsList(filters);
            } else {
                response = await AutomaticDebitsRejectedService.getRejectedDebitsList(filters);
            }
            if (response?.lotes?.length) {
                const details = await Promise.all(
                    response.lotes.map(lote => AutomaticDebitsRejectedService.getRejectedDetailsList(filters, lote.numeroPreliquidacion))
                );
                const combinedRejects = details.flatMap(res => res?.rechazos || []);
                setRejectedTransactionsList({ rechazos: combinedRejects });
                setRejectedTransactions(combinedRejects);
            } else {
                setRejectedTransactionsList({ rechazos: [] });
                setRejectedTransactions([]);
            }
        } catch {
            setShowError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const getCreditCardsEntityDescription = (id: string) => {
        const item = creditCardsCompaniesCombo.find((item) => item.value === id);
        return item ? item.label : '';
    };

    const getCreditCardList = useCallback(async () => {
        setIsLoadingFilters(true);
        try {
            const response = await AutomaticDebitsRejectedService.getCreditCardList();
            setCreditCardsCompaniesCombo(response.data);
        } catch (error) {
            setShowError(true);
        } finally {
            setIsLoadingFilters(false);
        }
    }, [setCreditCardsCompaniesCombo, setShowError]);

    const getRejectedtionsList = () => {
        const filters = formatFilters(form.getFieldsValue());
        setEmptyText(filters);
        fetchRejectedTransactions(filters);
    };

    useEffect(() => {
        if (type === REJECTIONS_TYPES_VALUES.CREDITO) {
            getFiltersInfo();
        }
    }, []);

    const getFiltersInfo = useCallback(() => {
        Promise.all([getCreditCardList()])
            .then(() => {
                setIsLoadingFilters(false);
            })
            .catch(error => { });
    }, [getCreditCardList]);

    useEffect(() => {
        if (rejectedTransactionsList) {
            (gridRef.current as any).setPage(1);
        }
    }, [rejectedTransactionsList, setRejectedTransactionsList]);

    const filters = useMemo(() => getInputsFilter(creditCardsCompaniesCombo, type), [creditCardsCompaniesCombo, type]);

    return (
        <div className="rejected-payments">
            <RowSize rowProps={{ gutter: [0, 0] }} className="rejected-payments__filter-row">
                {<InputFactory
                    form={form}
                    items={filters}
                    onFilter={getRejectedtionsList}
                    isLoading={isLoadingFilters}
                    initialState={initialStateFilters}
                />}
            </RowSize>
            {
                showError
                    ? <ErrorResults title="No se pudo cargar el rechazos de débitos" />
                    : (
                        <RowSize rowProps={{ gutter: [15, 15], className: 'rejected-credits-tab' }}>
                            <ColSize size={100}>
                                <Grid
                                    list={(rejectedTransactionsList?.rechazos) || []}
                                    setState={setRejectedTransactions}
                                    name="rechazos"
                                    ref={gridRef}
                                >
                                    <ColSize size={100}>
                                        {
                                            rejectedTransactions?.length
                                                ? rejectedTransactions.map((rejects, index) => (
                                                    <RejectedDebitsCard rejections={rejects} loading={isLoading} tabType={type} key={index} />
                                                ))
                                                : isLoading
                                                    ? (
                                                        <RowSize rowProps={{ gutter: [15, 15] }}>
                                                            <CardSkeletons />
                                                        </RowSize>
                                                    ) : <ItemNotFound textDescription={emptyStateText} imageUrl={InsurancePolicyNotFound} />
                                        }
                                    </ColSize>
                                </Grid>
                            </ColSize>
                        </RowSize>
                    )
            }
        </div>
    );
});
